// Module 08 (worker) — the postMessage wire protocol (sim-worker.v1). Two discriminated unions on a
// `t` tag, plus the lossless Snapshot pack/unpack and a return-trip BufferPool. Numeric vectors (θ
// per node, branch flowMw/loadingPct) travel as transferable Float64Array buffers (lossless — the
// |Δθ|≤1e-6 golden margin survives transport; Float32 would not); everything else is a structured-
// clone meta object. Ids never go on the wire — the worker emits index-ordered numbers and the
// consumer (Module 09) supplies the static nodeIds/edgeIds ordering to reassemble a Snapshot.
// Boundary (sim-worker.v1): import only this module, `domain`, `topology`, `engine`, or npm.
import type { Snapshot, Command, NodeId, EdgeId, BranchFlow } from "../domain/index";

// ── tagged-union messages ──────────────────────────────────────────────────────
export type MainToWorker =
  | { t: "init"; modelUrl: string }
  | { t: "start"; speed: number }
  | { t: "stop" }
  | { t: "setSpeed"; speed: number }
  | { t: "command"; cmd: Command }
  | { t: "recycle"; buffers: ArrayBuffer[] };

export type WorkerToMain =
  | { t: "ready" }
  | { t: "snapshot"; packed: PackedSnapshot }
  | { t: "error"; message: string };

// ── packed snapshot ────────────────────────────────────────────────────────────
// Everything in a Snapshot except the id-keyed numeric collections (nodeAngles, busVMagPu, branchFlows).
export type SnapshotMeta = Omit<Snapshot, "nodeAngles" | "busVMagPu" | "branchFlows">;

export interface PackedSnapshot {
  nodeAngles: Float64Array<ArrayBuffer>;       // θ in nodeIds order
  busVMagPu: Float64Array<ArrayBuffer>;        // engine.v3 P7: √w (LinDistFlow voltage magnitude) in nodeIds order
  branchFlowMw: Float64Array<ArrayBuffer>;     // flowMw in edgeIds order
  branchLoadingPct: Float64Array<ArrayBuffer>; // loadingPct in edgeIds order
  branchEnergized: Float64Array<ArrayBuffer>;  // energized 0|1 in edgeIds order (engine-driven flow viz)
  branchInService: Float64Array<ArrayBuffer>;  // inService 0|1 in edgeIds order (live circuit, solid line even at 0 flow)
  branchContingencyPct: Float64Array<ArrayBuffer>; // worst N-1 loading fraction in edgeIds order; NaN = not screened
  // engine.v3 P-viz (Phase 1.3+1.5, combined wire bump): these 3 were produced by dc-solve.ts/fade-clock.ts
  // every tick already but DROPPED here — the adapter recomputed flowMag/flowDir client-side (a fallback
  // that silently diverges from the real engine value under DLR line-rating derates) and islandId never
  // reached the map at all (no island-coloring channel existed). Now transported for real.
  branchIslandId: Float64Array<ArrayBuffer>;   // connected-component id in edgeIds order; -1 = de-energized
  branchFlowMag: Float64Array<ArrayBuffer>;    // |flowMw|/rateMva (DLR-derated), clamped 0..1, in edgeIds order
  branchFlowDir: Float64Array<ArrayBuffer>;    // sign(flowMw) as -1|0|1, in edgeIds order
  meta: SnapshotMeta;                          // structured-clone scalars/arrays
}

// Static index→id ordering, supplied once by the consumer (worker derives it from its own model).
export interface PackContext {
  nodeIds: readonly NodeId[];
  edgeIds: readonly EdgeId[];
}

function take(pool: BufferPool | undefined, length: number): Float64Array<ArrayBuffer> {
  return pool ? pool.acquireFloat64(length) : new Float64Array(length);
}

export function packSnapshot(
  s: Snapshot, ctx: PackContext, pool?: BufferPool,
): { packed: PackedSnapshot; transfer: ArrayBuffer[] } {
  const nN = ctx.nodeIds.length;
  const nE = ctx.edgeIds.length;

  const nodeAngles = take(pool, nN);
  for (let i = 0; i < nN; i++) nodeAngles[i] = s.nodeAngles.get(ctx.nodeIds[i]!) ?? 0;
  // engine.v3 P7: √w in nodeIds order. Default 1 (not 0) for a missing entry — a bus absent from the map is
  // treated as at-reference voltage, not blacked-out (0 is reserved for an explicitly de-energized bus).
  const busVMagPu = take(pool, nN);
  for (let i = 0; i < nN; i++) busVMagPu[i] = s.busVMagPu.get(ctx.nodeIds[i]!) ?? 1;

  const flowByEdge = new Map<EdgeId, BranchFlow>();
  for (const b of s.branchFlows) flowByEdge.set(b.edgeId, b);
  const branchFlowMw = take(pool, nE);
  const branchLoadingPct = take(pool, nE);
  const branchEnergized = take(pool, nE);
  const branchInService = take(pool, nE);
  const branchContingencyPct = take(pool, nE);
  const branchIslandId = take(pool, nE);
  const branchFlowMag = take(pool, nE);
  const branchFlowDir = take(pool, nE);
  for (let i = 0; i < nE; i++) {
    const b = flowByEdge.get(ctx.edgeIds[i]!);
    branchFlowMw[i] = b ? b.flowMw : 0;
    branchLoadingPct[i] = b ? b.loadingPct : 0;
    branchEnergized[i] = b && b.energized ? 1 : 0;
    // inService falls back to energized when an older snapshot omits it (energized ⇒ definitely in service).
    branchInService[i] = b && (b.inService ?? b.energized) ? 1 : 0;
    branchContingencyPct[i] = b && b.contingencyLoadingPct !== undefined ? b.contingencyLoadingPct : NaN;
    // -1 (de-energized/no island) is a valid real value, so fall back to it, not 0 (0 is a real island id).
    branchIslandId[i] = b && b.islandId !== undefined ? b.islandId : -1;
    branchFlowMag[i] = b && b.flowMag !== undefined ? b.flowMag : 0;
    branchFlowDir[i] = b && b.flowDir !== undefined ? b.flowDir : 0;
  }

  // AR2 (Phase 1, ★B structural fix): `meta` is the WHOLE Snapshot MINUS the two id-keyed numeric collections
  // (nodeAngles + branchFlows, which travel as typed arrays). Derived by EXCLUSION (object rest) — NOT a
  // hand-maintained allowlist — so any field added to `Snapshot` rides the wire automatically and can never be
  // silently dropped the way `openPorts` was. The exact field-set is locked by the contract test in
  // protocol.test.ts; the type `SnapshotMeta = Omit<Snapshot,"nodeAngles"|"branchFlows">` is the single source.
  // engine.v3 P7: busVMagPu joins nodeAngles/branchFlows as an EXCLUDED id-keyed collection (rides as a typed
  // array, not in meta) — so the meta key-set is UNCHANGED and the field-set contract test stays green.
  const { nodeAngles: _na, busVMagPu: _bv, branchFlows: _bf, ...meta } = s;

  return {
    packed: {
      nodeAngles, busVMagPu, branchFlowMw, branchLoadingPct, branchEnergized, branchInService, branchContingencyPct,
      branchIslandId, branchFlowMag, branchFlowDir, meta,
    },
    transfer: [nodeAngles.buffer, busVMagPu.buffer, branchFlowMw.buffer, branchLoadingPct.buffer, branchEnergized.buffer,
      branchInService.buffer, branchContingencyPct.buffer, branchIslandId.buffer, branchFlowMag.buffer, branchFlowDir.buffer],
  };
}

export function unpackSnapshot(p: PackedSnapshot, ctx: PackContext): Snapshot {
  const nodeAngles = new Map<NodeId, number>();
  const busVMagPu = new Map<NodeId, number>(); // engine.v3 P7: rebuild √w in nodeIds order
  for (let i = 0; i < ctx.nodeIds.length; i++) {
    nodeAngles.set(ctx.nodeIds[i]!, p.nodeAngles[i]!);
    busVMagPu.set(ctx.nodeIds[i]!, p.busVMagPu[i]!);
  }
  const branchFlows: BranchFlow[] = [];
  for (let i = 0; i < ctx.edgeIds.length; i++) {
    const cont = p.branchContingencyPct[i]!;
    const dir = p.branchFlowDir[i]!;
    branchFlows.push({
      edgeId: ctx.edgeIds[i]!, flowMw: p.branchFlowMw[i]!, loadingPct: p.branchLoadingPct[i]!,
      energized: p.branchEnergized[i] === 1 ? 1 : 0,
      inService: p.branchInService[i] === 1 ? 1 : 0,
      ...(Number.isNaN(cont) ? {} : { contingencyLoadingPct: cont }),
      islandId: p.branchIslandId[i]!,
      flowMag: p.branchFlowMag[i]!,
      flowDir: dir === 1 ? 1 : dir === -1 ? -1 : 0,
    });
  }
  return { ...p.meta, nodeAngles, busVMagPu, branchFlows };
}

// ── return-trip buffer pool ────────────────────────────────────────────────────
// A transferred ArrayBuffer detaches on the sender; the main thread posts it back via { t:"recycle" }
// and we reuse it. Keyed by byteLength so an acquire only ever reuses an exactly-sized buffer.
export class BufferPool {
  private readonly free = new Map<number, ArrayBuffer[]>();

  acquireFloat64(length: number): Float64Array<ArrayBuffer> {
    const byteLength = length * Float64Array.BYTES_PER_ELEMENT;
    const bucket = this.free.get(byteLength);
    const buf = bucket && bucket.length > 0 ? bucket.pop()! : new ArrayBuffer(byteLength);
    return new Float64Array(buf);
  }

  release(buf: ArrayBuffer): void {
    const bucket = this.free.get(buf.byteLength);
    if (bucket) bucket.push(buf);
    else this.free.set(buf.byteLength, [buf]);
  }

  size(): number {
    let n = 0;
    for (const bucket of this.free.values()) n += bucket.length;
    return n;
  }
}
